import { memo, useState } from 'react'
import {
  Image,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Alert,
  useWindowDimensions
} from 'react-native'
import { AntDesign, Ionicons, MaterialCommunityIcons } from '@expo/vector-icons'
import * as ImagePicker from 'expo-image-picker'
import * as DocumentPicker from 'expo-document-picker'
import * as WebBrowser from 'expo-web-browser'
import { startActivityAsync } from 'expo-intent-launcher'

import { Linking } from 'react-native'

// styles
import { boardStyles } from '../../static/styles/board'
import { colorPalette, globalStyles } from '../../static/styles/global'

// components
import ScanOrUploadDocumentModal from './ScanOrUploadDocumentModal'

// types
import { Form } from '../../types/onboardingTypes'
import ModalPopup from '../shared/modal/ModalPopup'
import { excludeExtensions, getFormData } from '../../helpers/onboardingHelper'

// stores
import { useFormsStore } from '../../stores/onboarding'

// constants
import { FILE_EXTENSIONS } from '../../constants/onboardingConstants'
import ModalAlert from '../shared/modal/ModalAlert'

// utils
import { AlertWeb } from '../../utils'
//translation
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { useHomeStore } from '../../stores/home'
import CustomButtonThree from '../shared/button/ButtonThree'

type ToolTipProps = {
  title?: string
  visible: boolean
  setVisible: any
  information?: string
}

// option tooltip
const OptionalToolTip = ({
  title,
  visible,
  setVisible,
  information
}: ToolTipProps): JSX.Element => {
  return (
    <ModalPopup visible={visible}>
      <View style={styles.modalHeader}>
        <Text
          style={[
            globalStyles.text,
            globalStyles.fs18,
            globalStyles.fwBold,
            globalStyles.blackTextColor
          ]}
        >
          {title}
        </Text>
        <TouchableOpacity onPress={() => setVisible(!visible)}>
          <Ionicons name="close-sharp" size={24} color={colorPalette.black} />
        </TouchableOpacity>
      </View>
      <View style={[styles.modalBody]}>
        <View style={[styles.modalBodyInner]}>
          <View style={[globalStyles.mb1]}>
            <Text style={[globalStyles.text]}>{information}</Text>
          </View>
        </View>
      </View>
    </ModalPopup>
  )
}

// resubmit tooltip
const ResubmitToolTip = ({
  title,
  visible,
  setVisible,
  information
}: ToolTipProps): JSX.Element => {
  return (
    <ModalPopup visible={visible}>
      <View style={styles.modalHeader}>
        <Text
          style={[
            globalStyles.text,
            globalStyles.fs18,
            globalStyles.fwBold,
            globalStyles.blackTextColor
          ]}
        >
          {title}
        </Text>
        <TouchableOpacity onPress={() => setVisible(!visible)}>
          <Ionicons name="close-sharp" size={24} color={colorPalette.black} />
        </TouchableOpacity>
      </View>
      <View style={[styles.modalBody]}>
        <View style={[styles.modalBodyInner]}>
          <View style={[globalStyles.mb1]}>
            <Text style={[globalStyles.text]}>{information}</Text>
          </View>
        </View>
      </View>
    </ModalPopup>
  )
}

// read form component
const ReadForm = ({
  essUserOnBoardingKey,
  essUserOnBoardingFormKey,
  readDocumentName,
  formStatusValue,
  formStatusFriendly,
  description,
  url,
  essFormKey
}: any) => {
  const [isFormPreviewed, setIsFormPreviewed] = useState(false)

  // stores' data
  const { markDocAsRead, previewReadonlyForms } = useFormsStore()

  const handleReadForm = () => {
    markDocAsRead(essUserOnBoardingKey, essUserOnBoardingFormKey)
  }


  const handlePreviewForm = async () => {
    setIsFormPreviewed(true)
    try {
      const response = await previewReadonlyForms(
        essFormKey,

      )
      const blob = new Blob([response], { type: 'application/pdf' })

      const url = URL.createObjectURL(blob)

      // Linking.openURL(url)
      if (Platform.OS === 'android') {
        startActivityAsync('android.intent.action.VIEW', {
          type: 'application/pdf',
          data: url
        })
      } else {
        WebBrowser.openBrowserAsync(url)
      }


    } catch (error) {
      console.error('Error:', error)
    }


  }
  const { t } = useTranslation()

  const { width } = useWindowDimensions()
  const isSmallScreen = width < 400

  return (
    <View style={[boardStyles.boardItemDetails, { flexDirection: isSmallScreen ? 'column' : 'row', alignItems: 'flex-start' }]}>
      <Text style={[globalStyles.fwBold, { flexShrink: 1, marginBottom: isSmallScreen ? 15 : 0 }]}>{description}</Text>

      {formStatusValue === 'N' && (
        <View style={{ alignItems: 'flex-end', width: isSmallScreen ? '100%' : 'auto' }}>
          <View style={{
            flexDirection: isSmallScreen ? 'row' : 'column-reverse',
            alignItems: isSmallScreen ? 'center' : 'flex-end',
            justifyContent: isSmallScreen ? 'space-between' : 'flex-end',
            width: isSmallScreen ? '100%' : 'auto'
          }}>

            <TouchableOpacity
              onPress={handleReadForm}
              style={{ flexDirection: 'row', alignItems: 'center', marginTop: isSmallScreen ? 0 : 5 }}
              disabled={!isFormPreviewed}
            >
              <MaterialCommunityIcons
                name={'checkbox-blank-outline'}
                size={24}
                color={colorPalette.black}
                style={{ marginRight: 5, opacity: 0.2 }}
              />
              <Text style={[globalStyles.text, globalStyles.fs14, globalStyles.fwSemiBold, globalStyles.blackTextColor]}>
                {t('I have read this document')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={handlePreviewForm}
              style={{ marginLeft: isSmallScreen ? 15 : 0, marginBottom: isSmallScreen ? 0 : 5 }}
            >
              <Ionicons name="eye-outline" size={24} color={colorPalette.tertiary} />
            </TouchableOpacity>

          </View>
        </View>
      )}

      {formStatusValue === 'C' && (
        <View style={{ alignItems: isSmallScreen ? 'flex-start' : 'flex-end', width: isSmallScreen ? '100%' : 'auto' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: isSmallScreen ? 'flex-start' : 'flex-end', width: isSmallScreen ? '100%' : 'auto' }}>
            <TouchableOpacity
              onPress={handleReadForm}
              style={{ flexDirection: 'row', alignItems: 'center' }}
              disabled={true}
            >
              <MaterialCommunityIcons
                name="checkbox-marked"
                size={24}
                color={colorPalette.black}
                style={{ marginRight: 5 }}
              />
              <Text style={[globalStyles.text, globalStyles.fs14, globalStyles.fwSemiBold, globalStyles.blackTextColor]}>
                {t('Completed')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  )
}

// upload form component
const UploadForm = ({
  essUserOnBoardingKey,
  essUserOnBoardingFormKey,
  description,
  formStatusFriendly,
  isRequired,
  optionalInformation,
  formStatusValue,
  formRejectionDescription,
  formRejectionComment,
  allowedExtensions,
  essFormKey,
  canDownloadForm,
  url,
  completeOnline,
  formComponentName,
  customForm
}: any) => {
  const [optionalTooltipVisible, setOptionalTooltipVisible] = useState(false)
  const [resubmitToolTipVisible, setResubmitToolTipVisible] = useState(false)
  const [uploadVisible, setUploadVisible] = useState(false)
  const [alertVisible, setAlertVisible] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const { t } = useTranslation()
  const navigation = useNavigation<NativeStackNavigationProp<any>>()

  // logs
  // console.log('allowedExtensions in UploadForm', allowedExtensions)

  // stores' data
  const { postData, formList } = useFormsStore()
  const { downloadFile, submittedForm } = useFormsStore()

  // browse photo libray (supports JPG and PNG)
  const handlePickImage = async () => {
    // pick image
    const options = {
      allowsEditing: false,
      quality: 1
    }
    const result = await ImagePicker.launchImageLibraryAsync(options)

    // logs
    // console.log('image:', result.assets[0].uri)

    if (!result.canceled) {
      try {
        // get image extension only
        const allowedImageExtensions = excludeExtensions(
          allowedExtensions,
          FILE_EXTENSIONS
        )

        // logs
        // console.log('allowedExtensions:', allowedExtensions)
        // console.log('allowedImageExtensions:', allowedImageExtensions)

        // form data
        const formData = await getFormData(
          result.assets[0].uri,
          description,
          allowedImageExtensions
        )

        // send document to storage
        if (description == 'Direct Deposit') {
          let isdirectdeposit = true;

          await postData(essUserOnBoardingKey, essUserOnBoardingFormKey, isdirectdeposit, formData)
        }
        if (description != 'Direct Deposit') {
          let isdirectdeposit = false;

          await postData(essUserOnBoardingKey, essUserOnBoardingFormKey, isdirectdeposit, formData)
        }

        if (completeOnline && formList.length > 0 && !customForm) {
          navigation.navigate(formComponentName, {
            mode: 'upload',
            essUserOnBoardingFormKey,
            essUserOnBoardingKey
          })
        }

        else {
          navigation.replace('OnboardingForms')
        }


      } catch (error: any) {
        // console.log(error.message)
        if (Platform.OS === 'web') {
          AlertWeb(t('Image not supported'), error.message)
        } else {
          Alert.alert(t('Image not supported'), error.message)
        }
        // setAlertMessage(error.message)
        // setTimeout(() => {
        //   setAlertVisible(true)
        // }, 200)
      } finally {
        // hide modal
        setUploadVisible(false)
      }
    }
  }
  const handledownloadfile = async () => { }
  // browse files (supports JPG, PNG and PDF)
  const handlePickDocument = async () => {
    // pick file (includes binary files and images)
    const options = {}
    const result = await DocumentPicker.getDocumentAsync(options)

    // logs
    //console.log('image:', result)
    // console.log('allowedExtensions:', allowedExtensions)

    if (!result.canceled) {
      try {
        // form data
        const formData = await getFormData(
          result.assets[0].uri,
          description,
          allowedExtensions
        )

        // send document to storage
        // await postData(essUserOnBoardingKey, essUserOnBoardingFormKey, formData)
        if (description == 'Direct Deposit') {
          let isdirectdeposit = true;

          await postData(essUserOnBoardingKey, essUserOnBoardingFormKey, isdirectdeposit, formData)
        }
        if (description != 'Direct Deposit') {
          let isdirectdeposit = false;

          await postData(essUserOnBoardingKey, essUserOnBoardingFormKey, isdirectdeposit, formData)
        }

        if (completeOnline && formList.length > 0 && !customForm) {
          navigation.navigate(formComponentName, {
            mode: 'upload',
            essUserOnBoardingFormKey,
            essUserOnBoardingKey
          })
        }
        else {
          navigation.replace('OnboardingForms')
        }
      } catch (error: any) {
        // console.log(error.message)
        if (Platform.OS === 'web') {
          AlertWeb(t('File not supported'), error.message)
        } else {
          Alert.alert(t('File not supported'), error.message)
        }
      } finally {
        // hide modal
        setUploadVisible(false)
      }
    }
  }

  let statusColor: { color: string }
  switch (formStatusValue) {
    case 'N':
      statusColor = globalStyles.blackTextColor
      break
    case 'P':
      statusColor = globalStyles.blackTextColor
      break
    case 'R':
      statusColor = globalStyles.dangerTextColor
      break
    case 'C':
      statusColor = globalStyles.successTextColor
      break
    default:
      statusColor = globalStyles.blackTextColor
      break
  }

  const handleDownloadAndOpenPDF = async () => {
    try {
      const response = await submittedForm(
        essUserOnBoardingFormKey,
        essUserOnBoardingKey
      )
      const blob = new Blob([response], { type: 'application/pdf' })

      const url = URL.createObjectURL(blob)

      Linking.openURL(url)
    } catch (error) {
      console.error('Error:', error)
    }
  }
  const translatedFormStatusFriendly: any = formStatusFriendly
  ? t(`form_status.${formStatusFriendly}`, { defaultValue: formStatusFriendly })
  : '';

  const homestore = useHomeStore()
  const { width } = useWindowDimensions();
  const isSmallScreen = width < 450;

  return (
    <>
      <View style={[boardStyles.boardItemDetails, { flexDirection: 'column' }]}>
        <View
          style={{
            width: '100%',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap'
          }}
        >
          <Text style={[globalStyles.fwBold]}>{description}</Text>

          {!isRequired && (
            <TouchableOpacity
              onPress={() => setOptionalTooltipVisible(true)}
              disabled={optionalInformation === ''}
              style={{ marginLeft: 8 }}
            >
              <View style={[globalStyles.hDirection, { alignItems: 'center' }]}>
                <Text
                  style={[
                    globalStyles.text,
                    globalStyles.fs12,
                    globalStyles.grayTextColor,
                    globalStyles.mr1
                  ]}
                >
                  {t('optional')}
                </Text>
                <MaterialCommunityIcons
                  name="information"
                  size={18}
                  color={colorPalette.gray}
                />
              </View>
            </TouchableOpacity>
          )}
        </View>

        <View
          style={{
            flexDirection: isSmallScreen ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isSmallScreen ? 'flex-start' : 'center',
            marginTop: 15
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flexShrink: 1
            }}
          >
            {canDownloadForm && (
              <TouchableOpacity
                style={{ marginRight: 10 }}
                onPress={() => downloadFile(essFormKey)}
              >
                <AntDesign
                  name="download"
                  size={24}
                  color={colorPalette.tertiary}
                />
              </TouchableOpacity>
            )}
            {formStatusFriendly !== 'Resubmit Pending' && (
              <Text
                style={[
                  globalStyles.text,
                  globalStyles.fs14,
                  globalStyles.fwSemiBold,
                  homestore.homeData?.isOnboarded && formStatusValue === 'R'
                    ? globalStyles.blackTextColor
                    : statusColor,
                  { flexShrink: 1 }
                ]}
              >
                {homestore.homeData?.isOnboarded && formStatusValue === 'R'
                  ? t('Not Started')
                  : translatedFormStatusFriendly}
              </Text>
            )}
          </View>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: isSmallScreen ? 'flex-start' : 'flex-end',
              marginTop: isSmallScreen ? 15 : 0
            }}
          >
            {((!homestore.homeData?.isOnboarded &&
              (formStatusValue === 'R' || formStatusValue === 'P')) ||
              (homestore.homeData?.isOnboarded && formStatusValue !== 'N')) && (
                <TouchableOpacity
                  onPress={handleDownloadAndOpenPDF}
                  style={{
                    marginRight: 15,
                    marginBottom: isSmallScreen ? 10 : 0
                  }}
                >
                  <Ionicons
                    name="eye-outline"
                    size={24}
                    color={colorPalette.tertiary}
                  />
                </TouchableOpacity>
              )}

            {completeOnline &&
              ((!homestore.homeData?.isOnboarded &&
                (formStatusValue === 'N' ||
                  formStatusValue === 'R' ||
                  formStatusValue === 'P')) ||
                (homestore.homeData?.isOnboarded &&
                  (formStatusValue === 'R' || formStatusValue === 'N'))) && (
                <View
                  style={{
                    marginRight: 15,
                    marginBottom: isSmallScreen ? 10 : 0
                  }}
                >
                  <CustomButtonThree
                    text={t('Complete Online')}
                    color="primary"
                    onPress={() => {
                      navigation.navigate(formComponentName, {
                        essUserOnBoardingFormKey,
                        essUserOnBoardingKey,
                        essFormKey,
                        mode: 'online',
                        description
                      })
                    }}
                    icon="pencil"
                    iconPosition="left"
                  />
                </View>
              )}

            {/* Upload Icon */}
            {((!homestore.homeData?.isOnboarded &&
              (formStatusValue === 'N' ||
                formStatusValue === 'R' ||
                formStatusValue === 'P')) ||
              (homestore.homeData?.isOnboarded &&
                (formStatusValue === 'R' || formStatusValue === 'N'))) && (
                <TouchableOpacity
                  onPress={() => setUploadVisible(!uploadVisible)}
                  style={{ marginBottom: isSmallScreen ? 10 : 0 }}
                >
                  <AntDesign
                    name="upload"
                    size={24}
                    color={colorPalette.tertiary}
                  />
                </TouchableOpacity>
              )}
          </View>
        </View>
        {formStatusFriendly === 'Resubmit Pending' && (
          <View style={{ marginTop: 5 }}>
            <Text
              style={[globalStyles.tertiaryTextColor, { flexShrink: 1 }]}
            >{`${description} (${translatedFormStatusFriendly})`}</Text>
          </View>
        )}
        {formStatusValue === 'R' && (
          <View style={{ marginTop: 5 }}>
            <Text
              style={[
                globalStyles.text,
                globalStyles.fs11,
                globalStyles.grayTextColor,
                globalStyles.flexWrapped
              ]}
            >
              {t(formRejectionDescription)}
            </Text>
            {formRejectionComment !== '' && (
              <Text
                style={[globalStyles.tertiaryTextColor]}
              >{`${description} (${t(formStatusFriendly)})`}</Text>
            )}
          </View>
        )}
      </View>

      <ScanOrUploadDocumentModal
        title={`Scan or Upload ${description}`}
        visible={uploadVisible}
        setVisible={setUploadVisible}
        scanDocumentsAction={() => { }}
        photoLibraryAction={handlePickImage}
        importFilesAction={handlePickDocument}
        description={description}
        essUserOnBoardingKey={essUserOnBoardingKey}
        essUserOnBoardingFormKey={essUserOnBoardingFormKey}
      />
      <OptionalToolTip
        title={`${description}` + t('(Optional)')}
        visible={optionalTooltipVisible}
        setVisible={setOptionalTooltipVisible}
        information={optionalInformation}
      />
      <ResubmitToolTip
        title={`${description}` + t('(Resubmit)')}
        visible={resubmitToolTipVisible}
        setVisible={setResubmitToolTipVisible}
        information={formRejectionComment}
      />
      <ModalAlert
        title={t('File not supported')}
        message={alertMessage}
        visible={alertVisible}
        setVisible={setAlertVisible}
      />
    </>
  )
}

const FormCard = memo(
  ({
    essUserOnBoardingKey,
    essUserOnBoardingFormKey,
    description,
    isRead,
    readDocumentName,
    isRequired,
    optionalInformation,
    iconName,
    formStatusValue,
    formStatusFriendly,
    formRejectionValue,
    formRejectionDescription,
    formRejectionComment,
    allowedExtensions,
    essFormKey,
    canDownloadForm,
    url,
    completeOnline,
    formComponentName,
    customForm,
    formCategoryDescription
  }: Form): JSX.Element => {
    const navigation = useNavigation<NativeStackNavigationProp<any>>()

    const getIntials = (): string | React.JSX.Element => {

      if (description == "Direct Deposit") {
        return "DD"
      }
      else if (formCategoryDescription == "Corporate Forms") {
        return <Ionicons name="briefcase-outline" size={24} color="#fff" />

      }
      else if (formCategoryDescription == "Identification Images") {
        return <MaterialCommunityIcons
          name="card-account-details-outline"
          size={24}
          color="#fff"
        />
      }
      else {
        return description ? description.split(" ")[0] : " "
      }


    }

    return (
      <View style={[boardStyles.boardItem]}>
        {description === 'W-4 Form' || description === 'I-9 Form' ? (
          <View
            style={{
              width: 71,
              height: "100%",
              backgroundColor: '#0B495C',
              borderRadius: 10,
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Text
              style={{
                color: 'white',
                textAlign: 'center',
                fontSize: 22,
                fontWeight: 'bold',

              }}
            >
              {description === 'W-4 Form' ? 'W-4' : 'I-9'}
            </Text>
            <Text
              style={{
                color: 'white',
                textAlign: 'center',
                fontSize: 14,

              }}
            >
              Form
            </Text>
          </View>
        ) : (
          <>
            <View
              style={{
                width: 71,
                height: "100%",
                backgroundColor: '#0B495C',
                borderRadius: 10,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Text
                style={{
                  color: 'white',
                  justifyContent: 'center',
                  alignContent: 'center',
                  textAlign: 'center',
                  textAlignVertical: 'center',
                  fontSize: 18,
                }}
              >
                {getIntials()}
              </Text>
            </View>
          </>
        )}
        {/* </TouchableOpacity> */}

        {isRead ? (
          <ReadForm
            {...{
              essUserOnBoardingKey,
              essUserOnBoardingFormKey,
              readDocumentName,
              formStatusValue,
              formStatusFriendly,
              url,
              description,
              essFormKey
            }}
          />
        ) : (
          <>
            <UploadForm
              {...{
                essUserOnBoardingKey,
                essUserOnBoardingFormKey,
                description,
                formStatusFriendly,
                isRequired,
                optionalInformation,
                formStatusValue,
                formRejectionDescription,
                formRejectionComment,
                allowedExtensions,
                essFormKey,
                canDownloadForm,
                url,
                completeOnline,
                formComponentName,
                customForm
              }}
            />
          </>
        )}
      </View>
    )
  }
)

const styles = StyleSheet.create({
  icon: {
    width: 71,
    height: 71,
    borderRadius: 8
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderColor: colorPalette.darkLight
  },
  modalBody: {
    paddingVertical: 0,
    paddingHorizontal: 0
  },
  modalBodyInner: {
    padding: 16
  }
})

export default FormCard
